import React, { useEffect, useMemo, useState } from 'react';
import * as moment from 'moment';
import { IBillingPreparationReport } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { CellClassParams, ColDef, ColGroupDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, NewValueParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { Label, OverlayTrigger } from 'react-bootstrap';
import { OrganType } from '@nkr/enums/organ-type';
import { ReportWrapper } from './styles';
import { PopoverWrapper } from '@nkr/components/popover-template';
import { Link } from 'react-router-dom';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: IBillingPreparationReport[],
    organType?: OrganType,
    onGridReady: (e: GridReadyEvent) => void,
    onSort: (sort: string) => void,
    onActRmcrFeeChange: (params: NewValueParams) => void,
}

const Grid = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);

    const getTotals = () => {
        let totals = {
            isSummary: true,
            preselectAverage: 0,
            dataComplianceFailed: 0,
            cryoLate: 0,
            rexmLate: 0,
            preselectReversals: 0,
            surgicalDeclines: 0,
            surgicalDeclinesBillable: 0,
            lateVxm: 0,
            onTimeKidneyPickup: 0,
            netChainsStarted: 0,
            billableRccaps: 0,
            lateSerologies: 0,
            bloodDrawsOutsideWindow: 0,
            expectedMonthlyDbFee: 0,
            expectedMonthlyNkrFee: 0,
            rmcrFeeTotal: 0,
            lastMonthlyFee: 0,
            lastNkrFee: 0,
            isAllInYes: 0,
            isAllInNo: 0,
            isUDashYes: 0,
            monthlyFee: 0,
            monthlyFeeNotes: '',
            dashBillingActualFee: 0,
            voucherHoldersLast12Months: 0,
            voucherHoldersCumulative: 0,
            ncsGap: 0,
            referredDonorsUnprotected: 0,
            coldIschemiaLagTimes: 0,
            lateFinalXm: 0,
            billableContracts: 0,
            dsDiscountCprScore: 0,
            dsDiscountPercent: 0,
            dsDiscountProtectedDonors: false,
            dsDiscountProtectedEndDate: null,
            dsDiscountVouchersLast12: 0
        };

        let totalPreselectAverage = 0;
        let totalReferredDonorsUnprotected = 0;
        let totalLateFinalXm = 0;
        let totalColdIschemiaLagTimes = 0;

        props.data.forEach(row => {
            totalPreselectAverage += row.preselectAverage >= 0 ? row.preselectAverage : 0;
            totalReferredDonorsUnprotected += row.referredDonorsUnprotected;
            totalLateFinalXm += row.lateFinalXm;
            totalColdIschemiaLagTimes += row.coldIschemiaLagTimes;
            totals.dataComplianceFailed += row.dataComplianceFailed;
            totals.cryoLate += row.cryoLate;
            totals.preselectReversals += row.preselectReversals;
            totals.surgicalDeclines += row.surgicalDeclines;
            totals.surgicalDeclinesBillable += row.surgicalDeclinesBillable;
            totals.lateVxm += row.lateVxm;
            totals.onTimeKidneyPickup += row.onTimeKidneyPickup >= 0 && row.onTimeKidneyPickup < 70 ? 1 : 0;
            totals.netChainsStarted += row.netChainsStarted;
            totals.billableRccaps += row.billableRccaps;
            totals.lateSerologies += row.lateSerologies;
            totals.bloodDrawsOutsideWindow += row.bloodDrawsOutsideWindow;
            totals.expectedMonthlyDbFee += row.expectedMonthlyDbFee;
            totals.expectedMonthlyNkrFee += row.expectedMonthlyNkrFee;
            totals.rmcrFeeTotal += row.rmcrFeeTotal;
            totals.lastNkrFee += row.lastNkrFee;
            totals.monthlyFee += row.monthlyFee;
            totals.dashBillingActualFee += row.dashBillingActualFee;
            totals.isAllInYes += row.isAllIn ? 1 : 0;
            totals.isAllInNo += row.isAllIn == false ? 1 : 0;
            totals.isUDashYes += row.isUDash ? 1 : 0;
            totals.voucherHoldersLast12Months += row.voucherHoldersLast12Months;
            totals.voucherHoldersCumulative += row.voucherHoldersCumulative;
            totals.ncsGap += row.ncsGap;
            totals.billableContracts += row.hasUpdatedContract || row.isOutdatedContractBillable === false ? 0 : 1;
            totals.dsDiscountCprScore = null;
            totals.dsDiscountPercent = null;//+= row.donorShieldDiscount > 0 ? 1 : 0;
        });

        totals.onTimeKidneyPickup = 100 - totals.onTimeKidneyPickup;
        totals.preselectAverage = totalPreselectAverage / props.data.filter(x => x.preselectAverage >= 0).length;
        totals.referredDonorsUnprotected = totalReferredDonorsUnprotected;
        totals.lateFinalXm = totalLateFinalXm;
        totals.coldIschemiaLagTimes = totalColdIschemiaLagTimes;

        return totals;
    };

    // const apiRef = useRef<GridApi>(null);

    useEffect(() => {
        if (props.organType === OrganType.Liver && gridApi) {
            setTimeout(() => {
                try { gridApi!.sizeColumnsToFit(); } catch { }
            }, 0);
        }
    }, [props.organType]);

    // Columns shown when Liver is selected
    const LIVER_VISIBLE_COL_IDS = ['1', 'center', 'executiveSponsor', 'liaison', 'dsDiscountProtectedLiver', 'dsPercentProtectedLiver', 'isLiverPlDash', 'isBillableLiver'];
    // Liver-specific columns to hide in the Kidney view
    const LIVER_ONLY_COL_IDS = ['dsDiscountProtectedLiver', 'dsPercentProtectedLiver', 'isLiverPlDash', 'isBillableLiver'];
    // Wider base widths for the Liver view so they spread out (sizeColumnsToFit uses these as proportions)
    const LIVER_WIDTHS: { [key: string]: number } = {
        'center': 220,
        'executiveSponsor': 180,
        'liaison': 180,
        'dsDiscountProtectedLiver': 200,
        'dsPercentProtectedLiver': 220,
        'isLiverPlDash': 160,
        'isBillableLiver': 120
    };

    const applyOrganVisibility = (cols: (ColGroupDef | ColDef)[]): (ColGroupDef | ColDef)[] => {
        const isLiver = props.organType === OrganType.Liver;

        const mapDef = (def: ColDef): ColDef => {
            const colId = def.colId ?? '';
            if (isLiver) {
                const visible = LIVER_VISIBLE_COL_IDS.includes(colId);
                return {
                    ...def,
                    hide: !visible,
                    width: visible && LIVER_WIDTHS[colId] ? LIVER_WIDTHS[colId] : def.width,
                    suppressSizeToFit: colId === '1' ? true : def.suppressSizeToFit,
                };
            }
            return { ...def, hide: LIVER_ONLY_COL_IDS.includes(colId) };
        };

        return cols.map(col => {
            const group = col as ColGroupDef;
            if (group.children) {
                return { ...group, children: group.children.map(child => mapDef(child as ColDef)) };
            }
            return mapDef(col as ColDef);
        });
    };

    const columns: (ColGroupDef | ColDef)[] = [
        {
            headerName: 'Center Info',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: '1',
                    field: 'rank',
                    headerName: 'R#',
                    headerComponentFramework: (params: IHeaderParams) => <div>R</div>,
                    sortable: false,
                    width: 40,
                    pinned: 'left',
                    pinnedRowCellRendererFramework: () => <div></div>,
                    valueGetter: (params: ValueGetterParams) => params.data.isSummary ? '' : params.node.rowIndex + 1
                },
                {
                    colId: 'center',
                    field: 'centerAlias',
                    headerName: 'Center',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
                    width: 125,
                    sort: 'asc',
                    pinned: 'left',
                    comparator: Utility.stringSortComparator,
                    cellRendererFramework: (params: ICellRendererParams) => <Link to={`/center/${params.data.centerId}/profile/edit/admin`} target="_blank">{params.value}</Link>,
                    pinnedRowCellRendererFramework: (params: ICellRendererParams) => {
                        return <div className='text-right'>Totals &gt;&gt;</div>;
                    }
                },

                {
                    colId: 'executiveSponsor',
                    field: 'executiveSponsor',
                    headerName: 'Executive Sponsor',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Executive<br />Sponsor</SortableHeaderComponent>,
                    width: 85,
                    pinned: 'left',
                    valueGetter: (params: ValueGetterParams) => {
                        let data = params.data as IBillingPreparationReport;
                        return data.executiveSponsor != null ? data.executiveSponsor.abbreviation : '';
                    }
                },

                {
                    colId: 'liaison',
                    field: 'liaison',
                    headerName: 'Center Liaison',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Liaison</SortableHeaderComponent>,
                    width: 75,
                    pinned: 'left',
                    valueGetter: (params: ValueGetterParams) => {
                        let data = params.data as IBillingPreparationReport;
                        return data.liaison != null ? data.liaison.abbreviation : '';
                    }
                },
            ]
        },

        {
            headerName: "RMCR Major Items",
            headerClass: 'list-group-header',
            children: [

                {
                    colId: 'billableRccaps',
                    field: 'billableRccaps',
                    headerName: 'Billable Rccaps',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Billable<br />RCCAP</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },

                {
                    colId: 'preselectReversals',
                    field: 'preselectReversals',
                    headerName: 'Preselect Reversals',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>PS<br />Reversal</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },
            ]
        },

        {
            headerName: 'RMCR Minor Items',
            headerClass: 'list-group-header',
            children: [
                // 30 Day Preselect %
                {
                    colId: 'preselectAverage',
                    field: 'preselectAverage',
                    headerName: 'PS Avg Pct',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>PS<br />Avg<br />Pct</SortableHeaderComponent>,
                    width: 75,
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 && params.value < 95 ? 'list-cell-yellow' : 'list-cell',
                    cellRenderer: (params: ICellRendererParams) => {
                        if (params.value >= 0 && params.value < 100)
                            return parseFloat(params.value).toFixed(1)

                        if (params.value == 100) {
                            return '100';
                        }

                        return 'N/A';
                    },
                    sortingOrder: ['asc', 'desc', null],
                    comparator: (a, b, nodeA, nodeB) => {
                        const getVal = (val: number) => val > 0 && val < 95 ? (-10000 + val) : (val > 0 ? val : 10000);
                        return getVal(a) - getVal(b);
                    },
                    pinnedRowCellRenderer: (params: ICellRendererParams) => {
                        if (params.value >= 0 && params.value < 100) {
                            return parseFloat(params.value).toFixed(1);
                        }

                        if (params.value == 100) {
                            return '100';
                        }
                    }
                },

                // Data Compliance (Items)
                {
                    colId: 'dataComplianceFailed',
                    field: 'dataComplianceFailed',
                    headerName: 'DC Failed',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Data< br />Comp<br />Failed</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value >= 21 ? 'list-cell-yellow' : 'list-cell',
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },

                // Net Chains Started Score
                {
                    colId: 'ncsGap',
                    field: 'ncsGap',
                    headerName: 'NCS Score',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>NCS<br />Score</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['asc', 'desc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value < 0 && params.data.isAdpPartner == false ? 'list-cell-yellow' : 'list-cell',
                    comparator: (a, b, nodeA, nodeB) => {
                        const valA = nodeA.data.ncsGap < 0 && nodeA.data.isAdpPartner == false ? (-10000 + nodeA.data.ncsGap) : nodeA.data.ncsGap;
                        const valB = nodeB.data.ncsGap < 0 && nodeB.data.isAdpPartner == false ? (-10000 + nodeB.data.ncsGap) : nodeB.data.ncsGap;
                        return valA - valB;
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary === true ? <div>{parseFloat(params.value).toLocaleString()}</div> : (
                            <OverlayTrigger trigger={['hover', 'focus']} overlay={
                                <PopoverWrapper>
                                    <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 250 }}>
                                        <tbody>
                                            <tr>
                                                <td colSpan={3} className='bg-info text-center' style={{ borderTop: '0 none' }}>Details</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>NCS Actual</td>
                                                <td className='text-right'>{data.netChainsStarted}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>Unredeemed FV Donations</td>
                                                <td className='text-right'>{data.voucherHoldersCumulative}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>NCS Score</td>
                                                <td className='text-right'>{data.ncsGap}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </PopoverWrapper>
                            } rootClose>
                                <div style={{ textAlign: "right" }}>{data.ncsGap < 0 ? <span style={{ color: "red" }}>{data.ncsGap.toLocaleString()}</span> : data.ncsGap.toLocaleString()} &nbsp;<span className='glyphicon glyphicon-info-sign'></span></div>
                            </OverlayTrigger>
                        )
                    }
                },

                // Surgery Date Declines
                {
                    colId: 'surgicalDeclinesBillable',
                    field: 'surgicalDeclinesBillable',
                    headerName: 'Billable Surgical Declines',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params}>Billable<br />Surgical<br />Declines</SortableHeaderComponent>,
                    width: 80,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },

                {
                    colId: 'surgicalDeclines',
                    field: 'surgicalDeclines',
                    headerName: 'Total Surgical Declines',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params}>Total<br />Surgical<br />Declines</SortableHeaderComponent>,
                    width: 80,
                    sortingOrder: ['desc', 'asc', null],
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },

                // Donor Record Review & Virtual Crossmatch Results Recorded Late (> 4 Business Days)
                {
                    colId: 'lateVxm',
                    field: 'lateVxm',
                    headerName: 'Late DRR & VXM',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Late<br />DRR &amp;<br />VXM</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },

                // Final crossmatch results uploaded less than (3) days before recipient surgery.
                {
                    colId: 'lateFinalXm',
                    field: 'lateFinalXm',
                    headerName: 'Late Final XM',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params}>Late<br />Final<br />XM</SortableHeaderComponent>,
                    width: 75,
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport

                        if (params.value > 0 && !params.data.isSummary) {
                            return <Link to={`/center/${data.centerId}/reports/my-center/late-final-xm?startDate=${moment(data.invoiceDate).startOf('month').format('YYYY-MM-DD')}&endDate=${moment(data.invoiceDate).endOf('month').format('YYYY-MM-DD')}`} target="_blank">{parseFloat(params.value).toLocaleString()}</Link>;
                        }

                        return parseFloat(params.value).toLocaleString();
                    }
                },

                // Serology blood draw outside 5-7 day window
                {
                    colId: 'bloodDrawsOutsideWindow',
                    field: 'bloodDrawsOutsideWindow',
                    headerName: 'Late Serology Blood Draw',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Late<br />Serology<br /> Blood Draw</SortableHeaderComponent>,
                    width: 85,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },

                // Serology results uploaded less than three (3) days before donor surgery
                {
                    colId: 'lateSerologies',
                    field: 'lateSerologies',
                    headerName: 'Late Serologies',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Late<br />Serology<br />Upload</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport

                        if (params.value > 0 && !params.data.isSummary) {
                            return <Link to={`/center/${data.centerId}/reports/my-center/late-serologies?sd=${moment(data.invoiceDate).startOf('month').format('YYYY-MM-DD')}`} target="_blank">{parseFloat(params.value).toLocaleString()}</Link>;
                        }

                        return parseFloat(params.value).toLocaleString();
                    }
                },

                // On Time Kidney Pickup Last 3 Months
                {
                    colId: 'onTimeKidneyPickup',
                    field: 'onTimeKidneyPickup',
                    headerName: 'Late Kidney Pickup',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Late<br />Kidney<br />Pickup</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && (params.data.onTimeKidneyPickup >= 0 && params.data.onTimeKidneyPickup < 70) ? 'list-cell-yellow' : 'list-cell',
                    //INVERT % VALUE FROM onTime to Late
                    valueGetter: (params: ValueGetterParams) => { return params.data.onTimeKidneyPickup < 0 ? -1 : Math.floor(100 - params.data.onTimeKidneyPickup); },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary === true ? params.value : (params.value < 0
                            ? "N/A"
                            :
                            <OverlayTrigger trigger={['hover', 'focus']} overlay={
                                <PopoverWrapper>
                                    <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 250 }}>
                                        <tbody>
                                            <tr>
                                                <td colSpan={3} className='bg-info text-center' style={{ borderTop: '0 none' }}>Details</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}># On Time</td>
                                                <td className='text-right'>{data.onTimeKidneyPickupCount}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}># Included</td>
                                                <td className='text-right'>{data.onTimeKidneyPickupOfTotal}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>On-Time Pickup Rate</td>
                                                <td className='text-right'>{data.onTimeKidneyPickup}%</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </PopoverWrapper>
                            } rootClose>
                                <div style={{ textAlign: "right" }}>{`${params.value.toString()}%`} &nbsp;<span className='glyphicon glyphicon-info-sign'></span></div>
                            </OverlayTrigger>
                        )
                    }
                },

                {
                    colId: 'coldIschemiaLagTimes',
                    field: 'coldIschemiaLagTimes',
                    headerName: 'CIT Lag Time',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params}>CIT Lag<br />Time</SortableHeaderComponent>,
                    width: 75,
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport

                        if (params.value > 0 && !params.data.isSummary) {
                            return <Link to={`/center/${data.centerId}/reports/my-center/cold-ischemia-lag-time-detail`} target="_blank">{parseFloat(params.value).toLocaleString()}</Link>;
                        }

                        return parseFloat(params.value).toLocaleString();
                    }
                },

                {
                    colId: 'referredDonorsUnprotected',
                    field: 'referredDonorsUnprotected',
                    headerName: 'NKR Referred Donors w/o LD Prioritization',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params}>NKR Referred<br />Donors w/o<br />LD Prioritization</SortableHeaderComponent>,
                    width: 125,
                    cellClass: (params: CellClassParams) => params.data.isSummary == undefined && params.value > 0 ? 'list-cell-yellow' : 'list-cell',
                    sortingOrder: ['desc', 'asc', null],
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                },

                {
                    colId: 'hasUpdatedContract',
                    field: 'hasUpdatedContract',
                    headerName: 'Contract',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Updated<br />Contract</SortableHeaderComponent>,
                    width: 75,
                    cellClass: (params: CellClassParams) => params.data.isSummary == true || params.data.hasUpdatedContract
                        ? 'list-cell'
                        : (params.data.isOutdatedContractBillable == false ? 'list-cell list-cell-override' : 'list-cell list-cell-yellow'),
                    sortingOrder: ['asc', 'desc', ''],
                    cellRendererFramework: (params: ICellRendererParams) => {
                        return params.data.isSummary ? <>{params.data.billableContracts}</> : (params.value == 1 ? <span className="label label-success">Yes</span> : <span className={`label label-${params.data.isOutdatedContractBillable == false ? "default" : "warning"}`}>{params.data.isOutdatedContractBillable == false ? "Waive" : "No"}</span>);
                    },
                    valueGetter: (params: ValueGetterParams) => {
                        return params.data.hasUpdatedContract == true ? 1 : (params.data.isOutdatedContractBillable == false ? -1 : 0);
                    }
                },
            ]
        },
        {
            headerName: 'RMCR Fees',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'expectedMonthlyNkrFee',
                    field: 'expectedMonthlyNkrFee',
                    headerName: 'Est RMCR Fee',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>RMCR<br />Fee</SortableHeaderComponent>,
                    width: 100,
                    sortingOrder: ['desc', 'asc', null],
                    cellRenderer: (params: ICellRendererParams) => parseFloat(params.value).toLocaleString()
                }
            ]
        },

        {
            headerName: 'Monthly Fees',
            headerClass: 'list-group-header',
            children: [

                {
                    colId: 'monthlyFee',
                    field: 'monthlyFee',
                    headerName: 'Monthly Fee',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>NKR<br />Member<br />Center</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    valueGetter: (params: ValueGetterParams) => params.data.monthlyFee > 0 || (params.data.isAllIn && params.data.hadTxpsInLast12) ? params.data.monthlyFee : 99999,
                    cellClass: (params: CellClassParams) => params.data.monthlyFee > 0 || (params.data.isAllIn && params.data.hadTxpsInLast12) ? 'list-cell' : (params.data.isSuspended ? 'list-cell list-cell-override' : 'list-cell list-cell-danger'),
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary === true ? <div>{parseFloat(params.value).toLocaleString()}</div> : (
                            <OverlayTrigger trigger={['hover', 'focus']} overlay={
                                <PopoverWrapper>
                                    <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 250 }}>
                                        <tbody>
                                            <tr>
                                                <td colSpan={3} className='bg-info text-center' style={{ borderTop: '0 none' }}>Details</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>Equal Access Center?</td>
                                                <td className='text-right'>{data.isAllIn == true ? <Label bsStyle="success">YES</Label> : <Label bsStyle="warning">NO</Label>}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>TXPs Last 12?</td>
                                                <td className='text-right'>{data.hadTxpsInLast12 == true ? <Label bsStyle="success">YES</Label> : <Label bsStyle="warning">NO</Label>}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>Is Suspended?</td>
                                                <td className='text-right'>{data.isSuspended == false ? <Label bsStyle="success">NO</Label> : <Label bsStyle="danger">YES</Label>}</td>
                                            </tr>
                                            <tr>
                                                <td colSpan={2}>
                                                    <div><strong>NOTES:</strong></div>
                                                    <div>{data.monthlyFeeNotes}</div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </PopoverWrapper>
                            } rootClose>
                                <div style={{ textAlign: "center" }}>{parseFloat(data.monthlyFee.toString()).toLocaleString()} &nbsp;<span className='glyphicon glyphicon-info-sign'></span></div>
                            </OverlayTrigger>
                        )
                    }
                },

                {
                    colId: 'dashBillingActualFee',
                    field: 'dashBillingActualFee',
                    headerName: 'DASH',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>DASH</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.dashBillingActualFee > params.data.dashBillingExpectedFee
                        ? 'list-cell list-cell-warning'
                        : (params.data.dashBillingActualFee < params.data.dashBillingExpectedFee
                            ? 'list-cell list-cell-danger'
                            : 'list-cell'),
                    valueGetter: (params: ValueGetterParams) => params.data.dashBillingActualFee < params.data.dashBillingExpectedFee ? 99999 : params.data.dashBillingActualFee,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary === true ? <div>{parseFloat(params.value).toLocaleString()}</div> : (
                            <OverlayTrigger trigger={['hover', 'focus']} overlay={
                                <PopoverWrapper>
                                    <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 250 }}>
                                        <tbody>
                                            <tr>
                                                <td colSpan={3} className='bg-info text-center' style={{ borderTop: '0 none' }}>Details</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>Prior Year LDs</td>
                                                <td className='text-right'>{data.dashBillingPriorYearLds == null ? 'UNK' : parseFloat(data.dashBillingPriorYearLds.toString()).toLocaleString()}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>Expected Fee</td>
                                                <td className='text-right'>{data.dashBillingExpectedFee === undefined ? 'UNK' : parseFloat(data.dashBillingExpectedFee.toString()).toLocaleString()}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 500 }}>Actual Fee</td>
                                                <td className='text-right'>{data.dashBillingActualFee === undefined ? 'UNK' : parseFloat(data.dashBillingActualFee.toString()).toLocaleString()}</td>
                                            </tr>
                                            <tr>
                                                <td colSpan={2}>
                                                    <div><strong>NOTES:</strong></div>
                                                    <div>{data.dashBillingNotes}</div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </PopoverWrapper>
                            } rootClose>
                                <div style={{ textAlign: "center" }}>{parseFloat(data.dashBillingActualFee.toString()).toLocaleString()} &nbsp;<span className='glyphicon glyphicon-info-sign'></span></div>
                            </OverlayTrigger>
                        )
                    }
                },

                {
                    colId: 'isUDash',
                    field: 'isUDash',
                    headerName: 'Is UDASH?',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Is<br />UDASH?</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.data.isSummary
                        ? <>{params.data.isUDashYes}</>
                        : (params.value === true ? <span className="label label-success">Yes</span> : (params.value === false ? <span className="label label-warning">No</span> : <></>)),
                }
            ]
        },

        {
            headerName: 'Donor Shield Discount',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'dsDiscountCprScore',
                    field: 'dsDiscountCprScore',
                    headerName: 'CPR Score',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>CPR<br />Score</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary ? <></> : <Link to={`/center/${data.centerId}/reports/my-center/cpr-score-audit-report?endDate=${data.invoiceDate}`} target="_blank">{params.data.dsDiscountCprScore}%</Link>
                    },
                },
                {
                    colId: 'dsDiscountPercent',
                    field: 'dsDiscountPercent',
                    headerName: 'Discount',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Kidney<br />Discount</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.dsDiscountOverrideProtectedKidney ? 'list-cell list-cell-override' : 'list-cell',
                    comparator: (a, b, nodeA, nodeB) => {
                        const getVal = (node: any) => node.data.dsDiscountOverrideProtectedKidney === true ? 2000 : node.data.dsDiscountPercent * 10 + node.data.dsPercentProtectedKidney;
                        return getVal(nodeA) - getVal(nodeB);
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary ? <></> : (
                            <div style={{ textAlign: "center" }}>
                                <Link to={`/report/donors-protected?ctrID=${data.centerId}&endDate=${data.dsDiscountProtectedEndDate}`} target="_blank">{data.dsDiscountPercent}%</Link> &nbsp;
                                <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={
                                    <PopoverWrapper>
                                        <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 375 }}>
                                            <tbody>
                                                <tr>
                                                    <td colSpan={2} className='bg-info text-center' style={{ borderTop: '0 none' }}>Kidney Details</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 500 }}>CSTAR Data Through:</td>
                                                    <td className='text-right'>{data.dsDiscountProtectedEndDate}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 500 }}>% Protected?</td>
                                                    <td className='text-right'><Label bsStyle={data.dsDiscountProtectedDonors ? "success" : "danger"}>{data.dsPercentProtectedKidney}%</Label></td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 500 }}>Override % Protected Requirement?</td>
                                                    <td className='text-right'>{data.dsDiscountOverrideProtectedKidney == true ? <Label bsStyle="danger">Yes</Label> : <Label bsStyle="success">No</Label>}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 500 }}>SV Donors Last 12</td>
                                                    <td className='text-right'><Label bsStyle={data.dsDiscountVouchersLast12 >= 3 ? "success" : "danger"}>{data.dsDiscountVouchersLast12}</Label></td>
                                                </tr>
                                                <tr>
                                                    <td className='text-center' colSpan={2}><br />click link to audit donors protected</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </PopoverWrapper>
                                } rootClose>
                                    <span className='glyphicon glyphicon-info-sign' style={{ cursor: 'pointer' }}></span>
                                </OverlayTrigger>
                            </div>
                        )
                    }
                },
                {
                    colId: 'dsPercentProtectedKidney',
                    field: 'dsPercentProtectedKidney',
                    headerName: 'Kidney<br />% Protected',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Kidney<br />% Protected</SortableHeaderComponent>,
                    width: 95,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.dsDiscountOverrideProtectedKidney ? 'list-cell list-cell-override' : 'list-cell',
                    comparator: (a, b, nodeA, nodeB) => {
                        const getVal = (node: any) => node.data.dsDiscountOverrideProtectedKidney == true
                            ? 1000000
                            : node.data.dsPercentProtectedKidney * 1000 + node.data.dsDiscountPercent;
                        return getVal(nodeA) - getVal(nodeB);
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary ? <></> : (
                            <div style={{ textAlign: "center" }}>
                                <Link to={`/report/donors-protected?ctrID=${data.centerId}&endDate=${data.dsDiscountProtectedEndDate}`} target="_blank">{data.dsPercentProtectedKidney}%</Link> &nbsp;
                                <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={
                                    <PopoverWrapper>
                                        <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 375 }}>
                                            <thead>
                                                <tr>
                                                    <td colSpan={4} className='bg-info text-center' style={{ borderTop: '0 none' }}>Kidney Details</td>
                                                </tr>
                                                <tr>
                                                    <th></th>
                                                    <td className="text-center" style={{ padding: '8px 12px' }}>Protected</td>
                                                    <td className="text-center" style={{ padding: '8px 12px' }}>Total</td>
                                                    <td className="text-center" style={{ padding: '8px 12px' }}>% Protected</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Kidney Donors</td>
                                                    <td className="text-center">{data.kidneyDonorsProtected}</td>
                                                    <td className="text-center">{data.kidneyDonorsTotal}</td>
                                                    <td className="text-center">{data.dsPercentProtectedKidney}%</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={3} style={{ fontWeight: 500 }}>Override % Protected Requirement?</td>
                                                    <td className='text-right'>{data.dsDiscountOverrideProtectedKidney == true ? <Label bsStyle="danger">Yes</Label> : <Label bsStyle="success">No</Label>}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={3} style={{ fontWeight: 500 }}>SV Donors Last 12</td>
                                                    <td className='text-right'><Label bsStyle={data.dsDiscountVouchersLast12 >= 3 ? "success" : "danger"}>{data.dsDiscountVouchersLast12}</Label></td>
                                                </tr>
                                                <tr>
                                                    <td className='text-center' colSpan={4}><br />click link to audit donors protected</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </PopoverWrapper>
                                } rootClose>
                                    <span className='glyphicon glyphicon-info-sign' style={{ cursor: 'pointer' }}></span>
                                </OverlayTrigger>
                            </div>
                        )
                    }
                },
                {
                    colId: 'dsDiscountProtectedLiver',
                    field: 'dsDiscountProtectedLiver',
                    headerName: 'Liver Discount',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Liver<br />Discount</SortableHeaderComponent>,
                    width: 75,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.dsDiscountOverrideProtectedLiver ? 'list-cell list-cell-override' : 'list-cell',
                    valueGetter: (params: ValueGetterParams) => params.data.dsDiscountOverrideProtectedLiver === true ? 2 : (params.data.dsDiscountProtectedLiver === true ? 1 : 0),
                    comparator: (a, b, nodeA, nodeB) => {
                        const valA = nodeA.data.dsDiscountOverrideProtectedLiver === true ? 2000 : nodeA.data.dsPercentProtectedLiver;
                        const valB = nodeB.data.dsDiscountOverrideProtectedLiver === true ? 2000 : nodeB.data.dsPercentProtectedLiver;
                        return valA - valB;
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary ? <></> : (
                            <div style={{ textAlign: "center" }}>
                                {
                                    data.dsDiscountProtectedLiver
                                        ? <span className='label label-success'>Yes</span>
                                        : (data.dsPercentProtectedLiver == 0 ? <span className='label label-default'>N/A</span> : <span className='label label-warning'>No</span>)
                                }&nbsp;
                                <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={
                                    <PopoverWrapper>
                                        <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 375 }}>
                                            <tbody>
                                                <tr>
                                                    <td colSpan={2} className='bg-info text-center' style={{ borderTop: '0 none' }}>Liver Details</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 500 }}>CSTAR Data Through:</td>
                                                    <td className='text-right'>{data.dsDiscountProtectedEndDate}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 500 }}>% Protected?</td>
                                                    <td className='text-right'>{data.dsPercentProtectedLiver}%</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 500 }}>Override % Protected Requirement?</td>
                                                    <td className='text-right'>{data.dsDiscountOverrideProtectedLiver == true ? <span className='label label-danger'>Yes</span> : <span className='label label-success'>No</span>}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </PopoverWrapper>
                                } rootClose>
                                    <span className='glyphicon glyphicon-info-sign' style={{ cursor: 'pointer' }}></span>
                                </OverlayTrigger>
                            </div>
                        )
                    }
                },
                {
                    colId: 'dsPercentProtectedLiver',
                    field: 'dsPercentProtectedLiver',
                    headerName: 'Liver<br />% Protected',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Liver<br />% Protected</SortableHeaderComponent>,
                    width: 95,
                    sortingOrder: ['desc', 'asc', null],
                    cellClass: (params: CellClassParams) => params.data.dsDiscountOverrideProtectedLiver ? 'list-cell list-cell-override' : 'list-cell',
                    comparator: (a, b, nodeA, nodeB) => {
                        const valA = nodeA.data.dsDiscountOverrideProtectedLiver == true ? 2000 : nodeA.data.dsPercentProtectedLiver;
                        const valB = nodeB.data.dsDiscountOverrideProtectedLiver == true ? 2000 : nodeB.data.dsPercentProtectedLiver;
                        return valA - valB;
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IBillingPreparationReport
                        return params.data.isSummary ? <></> : (
                            <div style={{ textAlign: "center" }}>
                                <Link to={`/report/donors-protected?ctrID=${data.centerId}&endDate=${data.dsDiscountProtectedEndDate}`} target="_blank">{data.dsPercentProtectedLiver}%</Link> &nbsp;
                                <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={
                                    <PopoverWrapper>
                                        <table className='table table-striped table-condensed' style={{ marginBottom: 0, maxWidth: 375 }}>
                                            <thead>
                                                <tr>
                                                    <td colSpan={4} className='bg-info text-center' style={{ borderTop: '0 none' }}>Liver Details</td>
                                                </tr>
                                                <tr>
                                                    <th></th>
                                                    <td className="text-center" style={{ padding: '8px 12px' }}>Protected</td>
                                                    <td className="text-center" style={{ padding: '8px 12px' }}>Total</td>
                                                    <td className="text-center" style={{ padding: '8px 12px' }}>% Protected</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Liver Donors</td>
                                                    <td className="text-center">{data.liverDonorsProtected}</td>
                                                    <td className="text-center">{data.liverDonorsTotal}</td>
                                                    <td className="text-center">{data.dsPercentProtectedLiver}%</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={3} style={{ fontWeight: 500 }}>Override % Protected Requirement?</td>
                                                    <td className='text-right'>{data.dsDiscountOverrideProtectedLiver == true ? <span className='label label-danger'>Yes</span> : <span className='label label-success'>No</span>}</td>
                                                </tr>
                                                <tr>
                                                    <td className='text-center' colSpan={4}><br />click link to audit donors protected</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </PopoverWrapper>
                                } rootClose>
                                    <span className='glyphicon glyphicon-info-sign' style={{ cursor: 'pointer' }}></span>
                                </OverlayTrigger>
                            </div>
                        )
                    }
                }
            ]
        },
        {
            headerName: 'Monthly Fees',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'isLiverPlDash',
                    field: 'isLiverPlDash',
                    headerName: 'Is LPL DASH',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Is<br />LPL DASH</SortableHeaderComponent>,
                    width: 160,
                    cellClass: 'text-center',
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value === true ? <span className="label label-success">Yes</span> : (params.value === false ? <span className="label label-warning">No</span> : <></>),
                }
            ]
        },
        {
            headerName: 'Additional Details',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'isAdpPartner',
                    field: 'isAdpPartner',
                    headerName: 'Partner Center',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Partner<br />Center?</SortableHeaderComponent>,
                    width: 60,
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value === true ? <span className="label label-success">Yes</span> : (params.value === false ? <span className="label label-warning">No</span> : <></>),
                },

                {
                    colId: 'isDcn',
                    field: 'isDcn',
                    headerName: 'DCN?',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>DCN?</SortableHeaderComponent>,
                    width: 60,
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value === true ? <span className="label label-success">Yes</span> : (params.value === false ? <span className="label label-warning">No</span> : <></>),
                    pinnedRowCellRenderer: (params: ICellRendererParams) => `${params.data.isAllIn} Y : ${params.data.isAllIn} N`
                },

                {
                    colId: 'isAllIn',
                    field: 'isAllIn',
                    headerName: 'Equal Access?',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Equal<br />Access?</SortableHeaderComponent>,
                    width: 60,
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value === true ? <span className="label label-success">Yes</span> : (params.value === false ? <span className="label label-warning">No</span> : <></>),
                    pinnedRowCellRenderer: (params: ICellRendererParams) => `${params.data.isAllIn} Y : ${params.data.isAllIn} N`
                },

                {
                    colId: 'isBillableKidney',
                    field: 'isBillableKidney',
                    headerName: 'Is Billable (Kidney)',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Is<br />Billable (Kidney)?</SortableHeaderComponent>,
                    width: 120,
                    cellClass: (params: CellClassParams) => params.data.isSummary
                        ? 'list-cell text-center'
                        : (params.data.isBillableKidneyOverride == true ? 'list-cell list-cell-override text-center' : 'text-center'),
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value == true ? <span className="label label-success">Yes</span> : (params.value == false ? <span className="label label-warning">No</span> : <></>),
                    pinnedRowCellRenderer: (params: ICellRendererParams) => `${params.data.isBillableKidney} Y : ${params.data.isBillableKidney} N`
                },

                {
                    colId: 'isBillableLiver',
                    field: 'isBillableLiver',
                    headerName: 'Is Billable (Liver)',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Is<br />Billable (Liver)?</SortableHeaderComponent>,
                    width: 110,
                    cellClass: (params: CellClassParams) => params.data.isSummary
                        ? 'list-cell text-center'
                        : (params.data.isBillableLiverOverride == true ? 'list-cell list-cell-override text-center' : 'text-center'),
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value == true ? <span className="label label-success">Yes</span> : (params.value == false ? <span className="label label-warning">No</span> : <></>),
                    pinnedRowCellRenderer: (params: ICellRendererParams) => `${params.data.isBillableLiver} Y : ${params.data.isBillableLiver} N`
                },

                {
                    colId: 'isSuspended',
                    field: 'isSuspended',
                    headerName: 'Is Suspended',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Is<br />Susp'd?</SortableHeaderComponent>,
                    width: 60,
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value == true ? <span className="label label-danger">Yes</span> : (params.value === false ? <span className="label label-success">No</span> : <></>),
                },

                {
                    colId: 'hadTxpsInLast12',
                    field: 'hadTxpsInLast12',
                    headerName: 'TXPs Last 12',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>TXPs<br />Last<br />12?</SortableHeaderComponent>,
                    width: 60,
                    sortingOrder: ['desc', 'asc', null],
                    cellRendererFramework: (params: ICellRendererParams) => params.value === true ? <span className="label label-success">Yes</span> : (params.value === false ? <span className="label label-warning">No</span> : <></>)
                }
            ]
        },

    ];

    const columnDefs = useMemo(() => applyOrganVisibility(columns), [props.organType]);
    const rowData = useMemo(() => props.data, [props.data]);
    const totals = useMemo(() => getTotals(), [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false,
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={60}
                groupHeaderHeight={30}
                rowHeight={28}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No invoices for the selected month &amp; year...</div>}
                pinnedTopRowData={[totals]}
                onGridReady={(e) => {
                    setGridApi(e.api);
                    if (props.onGridReady) props.onGridReady(e);
                }}
                onFirstDataRendered={(e) => {
                }}
                getRowStyle={(params: any) => {
                    if (params.node.rowIndex == 0 && params.data.isSummary) {
                        return { background: 'lightblue', fontWeight: 600 } as any;
                    }
                }}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </ReportWrapper>
    );
}

export default Grid;